/**
 * patient-portal (10 — CLOUD-only) FeatureModule. Three lab-staff surfaces, all under /patient-portal/*:
 *
 *   /patient-portal/consentement — the consent-read: live central consent state (publication_novera)
 *   /patient-portal/publication  — the seam-4 publish gate (canPublishToNovera, default-closed)
 *   /patient-portal/resultats    — the walk-in result-view preview (verbatim emitted compte-rendu)
 *
 * Gating model. The three screens need DIFFERENT capabilities (consentement.read vs resultat.publish vs
 * dossier.lire), and the router applies ONE module-level gate per route. So — like inventory/finance —
 * the MODULE gate stays empty (any authenticated user reaches the shell) and we gate two ways:
 *   1. each NAV item carries its own requiredCapabilities (the side-nav shows only what you may open);
 *   2. each SCREEN renders its own ForbiddenState when its read/action capability is missing.
 * The server re-checks every request — useCan only hides/disables UI (never the trust boundary).
 *
 * Cloud-only (D-22). On a LOCAL install this module's /api/v1 routes are not mounted, so each probe
 * 404s; every screen then renders a calm "indisponible sur cette installation" panel rather than an
 * error (the tile stays visible but degrades gracefully).
 *
 * Capabilities used (exact server keys — server/src/modules/patient-portal/patient-portal.module.js,
 * server/src/config/rbac.config.js):
 *   patient-portal.consentement.read · patient-portal.resultat.publish · patient-portal.dossier.lire
 */
import type { FeatureModule } from "@/app";
import { MODULE_CAPS, PP_CAPS } from "./api";
import { registerPatientPortalMessages } from "./i18n";
import { ConsentReadPage } from "./screens/ConsentReadPage";
import { PublishGatePage } from "./screens/PublishGatePage";
import { ResultViewPage } from "./screens/ResultViewPage";

const patientPortalModule: FeatureModule = {
  id: "patient-portal",
  title: "Portail patient",
  // Empty module gate (see header): per-screen ForbiddenState + per-nav-item caps do the gating.
  requiredCapabilities: MODULE_CAPS,
  routes: [
    { path: "/patient-portal/consentement", element: <ConsentReadPage /> },
    { path: "/patient-portal/publication", element: <PublishGatePage /> },
    { path: "/patient-portal/resultats", element: <ResultViewPage /> },
  ],
  nav: [
    {
      id: "patient-portal-consentement",
      path: "/patient-portal/consentement",
      label: "nav.patient-portal.consentement",
      icon: "✓",
      order: 90,
      section: "nav.section.portail",
      requiredCapabilities: [PP_CAPS.consentementRead],
    },
    {
      id: "patient-portal-publication",
      path: "/patient-portal/publication",
      label: "nav.patient-portal.publication",
      icon: "☁",
      order: 91,
      section: "nav.section.portail",
      requiredCapabilities: [PP_CAPS.resultatPublish],
    },
    {
      id: "patient-portal-resultats",
      path: "/patient-portal/resultats",
      label: "nav.patient-portal.resultats",
      icon: "▤",
      order: 92,
      section: "nav.section.portail",
      requiredCapabilities: [PP_CAPS.dossierLire],
    },
  ],
  onRegister() {
    registerPatientPortalMessages();
  },
};

export default patientPortalModule;
